import {
  Alert,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  Paper,
  PasswordInput,
  SegmentedControl,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconShieldLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateScimToken, useRevokeScimToken, useSaveSsoSettings, useSsoSettings, type SsoSettings } from '@/api/sso';
import { useUpdateWorkspace } from '@/api/workspace';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';

/** Sicherheit des Workspaces: 2FA-Pflicht (SEC-03), Single Sign-on (SEC-04) und SCIM (SEC-05). */
export function SecuritySettings() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const update = useUpdateWorkspace(ctx.workspaceId);
  const onError = useApiError();
  const settings = ctx.workspace.settings;
  return (
    <Stack maw={860}>
      <Paper withBorder p="md">
        <Title order={5} mb="xs">
          {t('securitySettings.twoFactorTitle')}
        </Title>
        <Switch
          label={t('securitySettings.require2fa')}
          description={t('securitySettings.require2faHint')}
          checked={settings.security.require2fa}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ settings: { ...settings, security: { require2fa: e.currentTarget.checked } } }, { onError })}
        />
        {settings.security.require2fa && !ctx.user.twoFactorEnabled && (
          <Alert color="orange" mt="sm">
            {t('securitySettings.ownNo2fa')}
          </Alert>
        )}
      </Paper>
      {ctx.hasFeature('SSO') ? (
        <SsoPanel />
      ) : (
        <Alert color="gray" icon={<IconShieldLock size={18} />}>
          {t('securitySettings.ssoNotInPlan')}
        </Alert>
      )}
    </Stack>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  return (
    <TextInput
      label={label}
      value={value}
      readOnly
      rightSection={
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Button variant="subtle" size="compact-xs" onClick={copy} aria-label={t('common.copy')}>
              {copied ? t('common.copied') : <IconCopy size={14} />}
            </Button>
          )}
        </CopyButton>
      }
      rightSectionWidth={70}
    />
  );
}

function SsoPanel() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data } = useSsoSettings(ctx.workspaceId);
  if (!data) return null;
  return (
    <>
      <SsoForm key={JSON.stringify(data)} data={data} />
      <ScimPanel data={data} />
      <Text size="xs" c="dimmed">
        {t('securitySettings.ownerFallback')}
      </Text>
    </>
  );
}

function SsoForm({ data }: { data: SsoSettings }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const save = useSaveSsoSettings(ctx.workspaceId);
  const onError = useApiError();
  const [form, setForm] = useState({
    enabled: data.enabled,
    protocol: data.protocol,
    enforce: data.enforce,
    autoProvision: data.autoProvision,
    emailDomains: data.emailDomains,
    oidcIssuer: data.oidcIssuer ?? '',
    oidcClientId: data.oidcClientId ?? '',
    oidcClientSecret: '',
    samlIdpEntityId: data.samlIdpEntityId ?? '',
    samlSsoUrl: data.samlSsoUrl ?? '',
    samlCertificate: data.samlCertificate ?? '',
  });
  useEffect(() => setForm((f) => ({ ...f, oidcClientSecret: '' })), [data]);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const oidc = form.protocol === 'OIDC';

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Title order={5}>{t('securitySettings.ssoTitle')}</Title>
        <Badge color={data.enabled ? 'green' : 'gray'} variant="light">
          {data.enabled ? t('securitySettings.active') : t('securitySettings.inactive')}
        </Badge>
      </Group>
      <Stack>
        <SegmentedControl
          value={form.protocol}
          onChange={(v) => set('protocol', v)}
          data={[
            { value: 'OIDC', label: 'OpenID Connect' },
            { value: 'SAML', label: 'SAML 2.0' },
          ]}
          w="fit-content"
        />
        {oidc ? (
          <>
            <Text size="sm" c="dimmed">
              {t('securitySettings.oidcHint')}
            </Text>
            <CopyField label={t('securitySettings.redirectUri')} value={data.oidcRedirectUri} />
            <TextInput label={t('securitySettings.issuer')} placeholder="https://login.example.com/realms/acme" value={form.oidcIssuer} onChange={(e) => set('oidcIssuer', e.currentTarget.value)} />
            <TextInput label={t('securitySettings.clientId')} value={form.oidcClientId} onChange={(e) => set('oidcClientId', e.currentTarget.value)} />
            <PasswordInput
              label={t('securitySettings.clientSecret')}
              placeholder={data.oidcClientSecretSet ? t('securitySettings.secretKept') : ''}
              value={form.oidcClientSecret}
              onChange={(e) => set('oidcClientSecret', e.currentTarget.value)}
            />
          </>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              {t('securitySettings.samlHint')}
            </Text>
            <CopyField label={t('securitySettings.spEntityId')} value={data.samlSpEntityId} />
            <CopyField label={t('securitySettings.acsUrl')} value={data.samlAcsUrl} />
            <Button component="a" href={data.samlMetadataUrl} target="_blank" variant="default" size="xs" w="fit-content">
              {t('securitySettings.metadata')}
            </Button>
            <TextInput label={t('securitySettings.idpEntityId')} value={form.samlIdpEntityId} onChange={(e) => set('samlIdpEntityId', e.currentTarget.value)} />
            <TextInput label={t('securitySettings.ssoUrl')} placeholder="https://idp.example.com/sso/saml" value={form.samlSsoUrl} onChange={(e) => set('samlSsoUrl', e.currentTarget.value)} />
            <Textarea
              label={t('securitySettings.certificate')}
              description={t('securitySettings.certificateHint')}
              autosize
              minRows={4}
              maxRows={10}
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              value={form.samlCertificate}
              onChange={(e) => set('samlCertificate', e.currentTarget.value)}
            />
          </>
        )}
        <TagsInput
          label={t('securitySettings.domains')}
          description={t('securitySettings.domainsHint')}
          placeholder="example.com"
          value={form.emailDomains}
          onChange={(v) => set('emailDomains', v)}
          clearable
        />
        <Switch label={t('securitySettings.autoProvision')} description={t('securitySettings.autoProvisionHint')} checked={form.autoProvision} onChange={(e) => set('autoProvision', e.currentTarget.checked)} />
        <Switch label={t('securitySettings.enforce')} description={t('securitySettings.enforceHint')} checked={form.enforce} onChange={(e) => set('enforce', e.currentTarget.checked)} />
        <Switch label={t('securitySettings.enabled')} checked={form.enabled} onChange={(e) => set('enabled', e.currentTarget.checked)} />
        {data.enabled && <CopyField label={t('securitySettings.loginUrl')} value={data.loginUrl} />}
        <Group>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { ...form, oidcClientSecret: form.oidcClientSecret || undefined },
                { onSuccess: () => notifications.show({ color: 'green', message: t('common.saved') }), onError },
              )
            }
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

function ScimPanel({ data }: { data: SsoSettings }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const create = useCreateScimToken(ctx.workspaceId);
  const revoke = useRevokeScimToken(ctx.workspaceId);
  const onError = useApiError();
  const [token, setToken] = useState<string | null>(null);
  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>{t('securitySettings.scimTitle')}</Title>
        <Badge color={data.scimEnabled ? 'green' : 'gray'} variant="light">
          {data.scimEnabled ? t('securitySettings.active') : t('securitySettings.inactive')}
        </Badge>
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        {t('securitySettings.scimHint')}
      </Text>
      <Stack>
        <CopyField label={t('securitySettings.scimBaseUrl')} value={data.scimBaseUrl} />
        <Group>
          <Button
            variant={data.scimEnabled ? 'default' : 'filled'}
            loading={create.isPending}
            onClick={() => {
              const run = () => create.mutate(undefined, { onSuccess: (r) => setToken(r.token), onError });
              if (!data.scimEnabled) return run();
              modals.openConfirmModal({
                title: t('securitySettings.rotateTitle'),
                children: <Text size="sm">{t('securitySettings.rotateText')}</Text>,
                labels: { confirm: t('securitySettings.rotate'), cancel: t('common.cancel') },
                onConfirm: run,
              });
            }}
          >
            {data.scimEnabled ? t('securitySettings.rotate') : t('securitySettings.createToken')}
          </Button>
          {data.scimEnabled && (
            <Button variant="subtle" color="red" loading={revoke.isPending} onClick={() => revoke.mutate(undefined, { onError })}>
              {t('securitySettings.revokeToken')}
            </Button>
          )}
        </Group>
      </Stack>
      <Modal opened={!!token} onClose={() => setToken(null)} title={t('securitySettings.tokenTitle')} size="lg">
        <Stack>
          <Alert color="orange">{t('securitySettings.tokenOnce')}</Alert>
          <Code block>{token}</Code>
          <CopyButton value={token ?? ''}>
            {({ copied, copy }) => (
              <Button onClick={copy} leftSection={<IconCopy size={16} />}>
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
            )}
          </CopyButton>
        </Stack>
      </Modal>
    </Paper>
  );
}
